/**
 * Universal sync queue for all offline operations.
 *
 * - Stores pending actions in IndexedDB (survives page reload / app restart)
 * - Priority-based processing (status changes first, photos last)
 * - Deduplication via idempotencyKey (last-write-wins for text fields)
 * - Exponential backoff on server errors
 * - Singleton: not tied to any Vue component lifecycle
 */
import db from './offlineDb'
import { logError } from './errorLogger'
import { notify } from '@/composables/useNotify'
import { shouldDeferQueueItem } from './syncQueueDeps'

export {
  resolveQueueItemJobId,
  isCompletionSensitiveQueueItem,
  isJobPrerequisiteQueueItem,
  shouldDeferQueueItem,
} from './syncQueueDeps'

const STATUS = { PENDING: 'pending', PROCESSING: 'processing', FAILED: 'failed' }
const MAX_RETRIES = 10
// Max times an item may be recovered from PROCESSING back to PENDING. An item
// that crashes the app BEFORE any server response never increments `retries`,
// so without this cap it would loop forever at the priority head (a poison pill
// that blocks the whole queue). After this many recoveries we drop it like any
// other permanent loss.
const MAX_RECOVERIES = 3
const BASE_BACKOFF_MS = 2000

// Human-readable labels for queued actions that represent technician work.
// When one of these is permanently dropped from the queue (server rejected it,
// or it never went through after all retries) we MUST tell the technician —
// silently deleting it is exactly what produced the field reports of "the app
// didn't save my report / photos / dispatch status".
const ACTION_LABELS = {
  tech_dispatch: 'dispatch status',
  tech_arrive: 'arrival status',
  tech_done: 'job completion',
  tech_complete_part: 'job completion',
  tech_end_my_session: 'session end',
  tech_undispatch: 'dispatch cancellation',
  tech_pause: 'job pause',
  tech_resume: 'job resume',
  tech_continue: 'job continue',
  tech_clock_in: 'clock-in',
  tech_clock_out: 'clock-out',
  tech_reset_day: 'day reset',
  save_work_report: 'work report',
  save_device_report: 'device report',
  save_job_summary: 'job summary',
  save_notes: 'job notes',
  save_checklist: 'checklist',
  upload_photo: 'photos/videos',
  checkin_verify: 'arrival check-in',
  checkin_checkout: 'check-out',
  invoice_action: 'invoice change',
}

/**
 * Surface a permanently-lost queued action to the technician so they can
 * re-enter it, instead of losing it silently. Only notifies for the action
 * types that carry real field data (see ACTION_LABELS); housekeeping items are
 * dropped quietly (they are already written to the error log).
 */
function notifyPermanentLoss(item, reason) {
  const label = ACTION_LABELS[item?.type]
  if (!label) return
  try {
    notify.error(
      `Couldn't save your ${label} — ${reason}. Please re-enter it and try again once you're back online.`,
      10000
    )
  } catch { /* notification system not mounted yet — error already logged */ }
}

/**
 * Add action to the sync queue.
 * If an action with the same idempotencyKey already exists and is still
 * pending, its payload is replaced (last-write-wins) instead of adding a dupe.
 */
export async function enqueue(action) {
  const {
    type,
    url,
    method = 'POST',
    data = null,
    headers = {},
    idempotencyKey = null,
    priority = 3,
    formData = false,
    jobId = null,
  } = action

  const normalizedJobId = jobId != null && jobId !== ''
    ? String(jobId).replace(/^internal_/, '')
    : null

  if (idempotencyKey) {
    const existing = await db.syncQueue
      .where('idempotencyKey')
      .equals(idempotencyKey)
      .and(r => r.status === STATUS.PENDING)
      .first()

    if (existing) {
      await db.syncQueue.update(existing.id, {
        data,
        headers,
        jobId: normalizedJobId ?? existing.jobId ?? null,
        updatedAt: new Date().toISOString(),
      })
      console.log('[SyncQ] Updated existing queued action:', type, idempotencyKey)
      return existing.id
    }
  }

  const id = await db.syncQueue.add({
    type,
    url,
    method,
    data,
    headers,
    formData,
    jobId: normalizedJobId,
    idempotencyKey: idempotencyKey || null,
    priority,
    status: STATUS.PENDING,
    retries: 0,
    nextRetryAt: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  })

  console.log('[SyncQ] Enqueued:', type, '| id:', id, '| key:', idempotencyKey)
  return id
}

/**
 * Process the entire pending queue, ordered by priority then createdAt.
 * Returns { processed, failed, remaining }.
 */
export async function processQueue(apiClient) {
  // Recover orphaned items first. An item left in PROCESSING is from a previous
  // run that was interrupted before it got a response (app killed / tab closed
  // mid-request — very common on mobile). It was never confirmed by the server,
  // yet the normal query below only looks at PENDING/FAILED, so it would sit in
  // PROCESSING forever: silently lost. Requeue it so it gets another attempt.
  // A re-send is safe as long as the backend still has the idempotency key
  // cached (KEY_MAX_AGE ~2h); the SKIP_CACHE_PATHS endpoints (work-reports/save,
  // save-notes, save-checklist, devices/*) are not deduped at all but are
  // last-write-wins upserts, so a replay of the same payload is harmless there
  // too. Guard against a poison pill: an item that keeps crashing the app before
  // any response never bumps `retries`, so cap the recoveries and drop it after
  // MAX_RECOVERIES to unblock the priority head.
  const orphaned = await db.syncQueue.where('status').equals(STATUS.PROCESSING).toArray()
  for (const o of orphaned) {
    const recoveryCount = (o.recoveryCount || 0) + 1
    if (recoveryCount > MAX_RECOVERIES) {
      // Free the cached media for an upload we're giving up on.
      if (o.type === 'upload_photo' && o.data?._photoIds) {
        await db.cachedPhotos.bulkDelete(o.data._photoIds).catch(() => {})
      }
      await db.syncQueue.delete(o.id)
      console.warn('[SyncQ] Discarded poison-pill orphan after recoveries:', o.type, o.id, recoveryCount)
      notifyPermanentLoss(o, 'it kept interrupting before the server could confirm it')
      logError('sync_item_error', `Orphan recovered ${MAX_RECOVERIES} times for ${o.type}`, {
        action: `sync_poison_pill_${o.type}`,
        api_url: o.url,
        api_method: o.method,
        extra: { queue_id: o.id, recoveryCount, idempotencyKey: o.idempotencyKey },
      })
      continue
    }
    await db.syncQueue.update(o.id, { status: STATUS.PENDING, recoveryCount })
    console.warn('[SyncQ] Recovered orphaned processing item:', o.type, o.id, '| recovery:', recoveryCount)
  }

  const now = new Date().toISOString()
  const pending = await db.syncQueue
    .where('status')
    .anyOf(STATUS.PENDING, STATUS.FAILED)
    .and(r => !r.nextRetryAt || r.nextRetryAt <= now)
    .sortBy('priority')

  if (pending.length === 0) return { processed: 0, failed: 0, remaining: 0 }

  // Sort by priority ASC, then createdAt ASC (oldest first within same priority)
  pending.sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt))

  let processed = 0
  let failed = 0
  const completedIds = new Set()

  for (const item of pending) {
    if (shouldDeferQueueItem(item, pending, completedIds)) {
      console.log('[SyncQ] Deferred (job prerequisites pending):', item.type, item.id)
      continue
    }

    await db.syncQueue.update(item.id, { status: STATUS.PROCESSING })

    try {
      const config = {
        method: item.method,
        url: item.url,
        headers: { ...item.headers },
      }

      if (item.formData && item.data) {
        // Reconstruct FormData from stored blob references
        const fd = await _rebuildFormData(item)
        // A photo upload whose cached blobs are all gone would rebuild to an
        // EMPTY body and the backend would reject it with 400 "No files
        // provided". Discard such an orphan instead of replaying a doomed,
        // file-less request (this was a real source of the 400 upload errors).
        if (item.type === 'upload_photo' && !fd.has('photos') && !fd.has('videos')) {
          if (item.data?._photoIds) {
            await db.cachedPhotos.bulkDelete(item.data._photoIds).catch(() => {})
          }
          await db.syncQueue.delete(item.id)
          console.warn('[SyncQ] Discarded photo upload with no cached files left:', item.id)
          continue
        }
        config.data = fd
        config.headers['Content-Type'] = 'multipart/form-data'
      } else {
        config.data = item.data
      }

      if (item.idempotencyKey) {
        config.headers['X-Idempotency-Key'] = item.idempotencyKey
      }

      await apiClient.request(config)

      // Clean up associated cached photos if this was a photo upload
      if (item.type === 'upload_photo' && item.data?._photoIds) {
        await db.cachedPhotos.bulkDelete(item.data._photoIds)
      }

      await db.syncQueue.delete(item.id)
      processed++
      completedIds.add(item.id)
      console.log('[SyncQ] Synced:', item.type, item.id)
      if (item.type === 'save_work_report' || item.type === 'save_device_report') {
        const jobId = item.data?.job_id
        if (jobId) {
          window.dispatchEvent(new CustomEvent('fsa-work-data-synced', {
            detail: { job_id: String(jobId).replace('internal_', ''), type: item.type },
          }))
        }
      }
    } catch (err) {
      const status = err?.response?.status

      // Version conflict on replay: the expected_version stored at enqueue time
      // is stale by now. Do NOT discard the item (that permanently lost the
      // technician's offline work). Strip expected_version and retry once so the
      // server applies it last-write-wins (this is per-tech data — the only
      // editor is this technician, so there is no real concurrent writer).
      if (status === 409 && err?.response?.data?.error === 'version_conflict'
          && item.data && Object.prototype.hasOwnProperty.call(item.data, 'expected_version')) {
        const { expected_version, ...rest } = item.data
        await db.syncQueue.update(item.id, { status: STATUS.PENDING, data: rest })
        console.warn('[SyncQ] version_conflict on replay — retrying without expected_version:', item.type, item.id)
        continue
      }

      // 401/403 are authentication/authorization failures, NOT the server
      // rejecting the work itself. The tech's access token has likely expired
      // (or a refresh is mid-flight); discarding here would silently lose real
      // offline work and the "server rejected it" message would be a lie.
      // Treat them as retryable so a re-login recovers the queued work.
      const isAuthError = status === 401 || status === 403

      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429 && !isAuthError) {
        // Free the cached media for a rejected upload so we don't leak blobs.
        if (item.type === 'upload_photo' && item.data?._photoIds) {
          await db.cachedPhotos.bulkDelete(item.data._photoIds).catch(() => {})
        }
        await db.syncQueue.delete(item.id)
        failed++
        console.warn('[SyncQ] Discarded (4xx):', item.type, item.id, status)
        notifyPermanentLoss(item, 'the server rejected it')
        logError('sync_item_error', err, {
          action: `sync_discard_${item.type}`,
          api_url: item.url,
          api_method: item.method,
          api_status: status,
          api_response: err?.response?.data,
          extra: { queue_id: item.id, idempotencyKey: item.idempotencyKey },
        })
        continue
      }

      // Server error or network error — schedule retry with backoff
      const retries = (item.retries || 0) + 1
      if (retries >= MAX_RETRIES) {
        // Free the cached media for an upload we're giving up on.
        if (item.type === 'upload_photo' && item.data?._photoIds) {
          await db.cachedPhotos.bulkDelete(item.data._photoIds).catch(() => {})
        }
        await db.syncQueue.delete(item.id)
        failed++
        console.warn('[SyncQ] Max retries reached, discarded:', item.type, item.id)
        notifyPermanentLoss(item, "it couldn't reach the server")
        logError('sync_item_error', `Max retries (${MAX_RETRIES}) reached for ${item.type}`, {
          action: `sync_max_retries_${item.type}`,
          api_url: item.url,
          api_method: item.method,
          extra: { queue_id: item.id, retries, lastError: err?.message },
        })
        continue
      }

      const backoff = Math.min(BASE_BACKOFF_MS * Math.pow(2, retries), 300000) // cap 5 min
      const nextRetry = new Date(Date.now() + backoff).toISOString()

      await db.syncQueue.update(item.id, {
        status: STATUS.FAILED,
        retries,
        nextRetryAt: nextRetry,
        lastError: err?.message || 'Unknown error',
      })

      console.warn('[SyncQ] Will retry:', item.type, item.id, '| attempt:', retries, '| next:', nextRetry)
      // Stop processing further items — server might be down
      break
    }
  }

  const remaining = await db.syncQueue.where('status').anyOf(STATUS.PENDING, STATUS.FAILED).count()
  return { processed, failed, remaining }
}

/**
 * Get the count of pending items in the queue.
 */
export async function getPendingCount() {
  // Include PROCESSING so an orphaned (interrupted) item still counts as work to
  // do — otherwise the periodic poll would see 0 and never run the recovery that
  // resets it back to PENDING.
  return db.syncQueue.where('status').anyOf(STATUS.PENDING, STATUS.FAILED, STATUS.PROCESSING).count()
}

/**
 * Get all pending items (for UI display).
 */
export async function getPendingItems() {
  return db.syncQueue.where('status').anyOf(STATUS.PENDING, STATUS.FAILED, STATUS.PROCESSING).sortBy('priority')
}

/**
 * Clear the entire queue (use with caution).
 */
export async function clearQueue() {
  await db.syncQueue.clear()
  console.log('[SyncQ] Queue cleared')
}

/**
 * Remove a specific item from the queue.
 */
export async function removeItem(id) {
  await db.syncQueue.delete(id)
}

/**
 * Rebuild FormData for photo uploads from IndexedDB cached blobs.
 */
async function _rebuildFormData(item) {
  const fd = new FormData()
  const d = item.data || {}

  // Add all non-blob fields
  for (const [key, val] of Object.entries(d)) {
    if (key === '_photoIds' || key === '_blobs') continue
    fd.append(key, val)
  }

  // Rebuild media files from cachedPhotos table.
  if (d._photoIds && d._photoIds.length > 0) {
    for (const photoId of d._photoIds) {
      const cached = await db.cachedPhotos.get(photoId)
      if (!cached) continue

      // Reconstruct a Blob from the stored ArrayBuffer (new format) or fall
      // back to a legacy stored Blob (items queued before this fix).
      let blob = null
      if (cached.buffer) {
        blob = new Blob([cached.buffer], { type: cached.mimeType || 'application/octet-stream' })
      } else if (cached.blob) {
        blob = cached.blob
      }
      if (!blob) continue

      // Preserve photo vs video field + original filename/extension so the
      // backend stores a video as a video instead of a corrupt .jpg.
      const field = cached.field === 'videos' ? 'videos' : 'photos'
      const fallbackName = field === 'videos' ? 'video.mp4' : 'photo.jpg'
      fd.append(field, blob, cached.fileName || fallbackName)
    }
  }

  return fd
}
